import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSystem } from '@ohif/core';
import PageLayout from '../../components/layouts/PageLayout';
import authService from '../../auth/authService';
import { canAccessPage } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import { useDebounce } from '../../hooks/useDebounce';
import { getModalities } from '../../utils/modalityUtils';
import machineService from '../../services/machineService';
import { mapMachineToTableRow } from '../../utils/machineMapper';
import type { Machine, MachineColumnKey, MachineTableRow } from '../../types/machine.types';
import MachineRegisterDialog from './MachineRegisterDialog';
import MachineMaintenanceTab from './MachineMaintenanceTab';
import {
  DEFAULT_VISIBLE_COLUMNS,
  MACHINE_COLUMN_LABELS,
  MachineImagingTabBody,
  MachineTopbarActions,
  buildMachineTableColumns,
  markMachinesChecking,
  replaceMachineById,
  resolveMachineFromConnectivityResult,
} from './machineManagementHelpers';
import './MachineManagement.css';

type MachineHubTab = 'imaging' | 'maintenance';

const ITEMS_PER_PAGE = 10;

const MachineManagement: React.FC = () => {
  return <MachineManagementInner />;
};

function MachineManagementInner() {
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;

  const user = authService.getUser();
  const hasAccess = canAccessPage(user, '/machines');

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery.trim(), 400);
  const [currentPage, setCurrentPage] = useState(1);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [activeTab, setActiveTab] = useState<MachineHubTab>('imaging');
  const [maintenanceScheduleSignal, setMaintenanceScheduleSignal] = useState(0);
  const [columnVisibility, setColumnVisibility] =
    useState<Record<MachineColumnKey, boolean>>(DEFAULT_VISIBLE_COLUMNS);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsRef = useRef<HTMLDivElement>(null);
  const connectivityRequestIdRef = useRef(0);

  const modalityOptions = useMemo(() => {
    const stored = getModalities();
    if (stored?.length) {
      return stored.map(m => ({ value: m.name, label: m.name }));
    }
    return [
      { value: 'CT', label: 'CT' },
      { value: 'MR', label: 'MR' },
      { value: 'XR', label: 'XR' },
      { value: 'US', label: 'US' },
    ];
  }, []);

  const applyConnectivityResults = useCallback(async (list: Machine[]) => {
    if (list.length === 0) {
      return;
    }

    const requestId = ++connectivityRequestIdRef.current;

    // Show checking state while live status is resolved for each machine.
    setMachines(prev => markMachinesChecking(prev, list));
    const checkingIds = new Set(list.map(item => item.id));
    setSelectedMachine(prev =>
      prev && checkingIds.has(prev.id) ? { ...prev, connectivityStatus: 'Checking' } : prev
    );

    await Promise.all(
      list.map(async machine => {
        const result = await machineService.checkMachineConnectivity(machine.id, machine);
        if (connectivityRequestIdRef.current !== requestId) {
          return;
        }

        const updated = resolveMachineFromConnectivityResult(machine, result);

        setMachines(prev => replaceMachineById(prev, updated));
        setSelectedMachine(prev => (prev?.id === updated.id ? updated : prev));
      })
    );
  }, []);

  const fetchMachines = useCallback(async () => {
    setLoading(true);
    setError(null);
    connectivityRequestIdRef.current += 1;
    try {
      const offset = (currentPage - 1) * ITEMS_PER_PAGE;

      const result = await machineService.getAllMachines({
        offset,
        limit: ITEMS_PER_PAGE,
        ...(debouncedSearch
          ? {
              MachineName: debouncedSearch,
              AETitle: debouncedSearch,
              Modality: debouncedSearch,
              Location: debouncedSearch,
            }
          : {}),
      });

      if (result.success && result.data) {
        const { machines: list, hasMore } = result.data;
        setMachines(list);
        setTotalItems(hasMore ? offset + list.length + 1 : offset + list.length);
        setLoading(false);
        // Live Status column: check every loaded machine.
        void applyConnectivityResults(list);
      } else {
        setMachines([]);
        setTotalItems(0);
        setError(result.error || 'Failed to load machines.');
        setLoading(false);
      }
    } catch {
      setMachines([]);
      setTotalItems(0);
      setError('Failed to load machines.');
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, applyConnectivityResults]);

  useEffect(() => {
    fetchMachines();
  }, [fetchMachines]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
        setColumnsOpen(false);
      }
    };
    if (columnsOpen) {
      document.addEventListener('mousedown', handleOutside);
    }
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [columnsOpen]);

  const tableRows: MachineTableRow[] = useMemo(
    () => machines.map(mapMachineToTableRow),
    [machines]
  );

  const columns = useMemo(() => buildMachineTableColumns(columnVisibility), [columnVisibility]);

  const handleRowClick = useCallback(
    (row: MachineTableRow) => {
      const machine = machines.find(m => m.id === row.id);
      if (machine) {
        setSelectedMachine(machine);
      }
    },
    [machines]
  );

  const handleRegisterSuccess = useCallback(
    (machine: Machine) => {
      uiNotificationService.show({
        title: 'Machine registered',
        message: `${machine.machineName} has been added to the registry.`,
        type: 'success',
      });
      fetchMachines();
    },
    [fetchMachines, uiNotificationService]
  );

  const handleMachineConnectivityRefresh = useCallback((machine: Machine) => {
    setMachines(prev => prev.map(item => (item.id === machine.id ? machine : item)));
    setSelectedMachine(prev => (prev?.id === machine.id ? machine : prev));
  }, []);

  const handleEditSuccess = useCallback(
    (machine: Machine) => {
      setSelectedMachine(machine);
      uiNotificationService.show({
        title: 'Changes saved',
        message: `${machine.machineName} has been updated.`,
        type: 'success',
      });
      fetchMachines();
    },
    [fetchMachines, uiNotificationService]
  );

  const toggleableColumns = useMemo(
    () => Object.keys(MACHINE_COLUMN_LABELS) as MachineColumnKey[],
    []
  );

  const handleTabChange = useCallback((tab: MachineHubTab) => {
    setActiveTab(tab);
    if (tab !== 'imaging') {
      setSelectedMachine(null);
    }
  }, []);

  const handleMaintenanceNotify = useCallback(
    (opts: { title: string; message: string; type: 'success' | 'error' | 'info' }) => {
      uiNotificationService.show(opts);
    },
    [uiNotificationService]
  );

  const facilityName = authService.getFacility()?.facility_name || 'GIMS NOIDA';

  if (!hasAccess) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  return (
    <PageLayout
      title="Machines"
      hideTitleInHeader
      headerVariant="light"
      contentClassName="machine-management-page-content"
      breadcrumbs={[{ label: 'Home', path: '/admin-dashboard' }, { label: 'Machines' }]}
    >
      <div className="machine-management-page">
        <div className="machine-management-card">
          <div className="machine-management-topbar">
            <div className="machine-management-topbar-left">
              <h1 className="machine-management-page-title">Machines</h1>
              <fieldset
                className="machine-view-toggle"
                aria-label="View mode"
              >
                <button
                  type="button"
                  className="machine-view-toggle-btn machine-view-toggle-btn--active"
                  aria-pressed="true"
                >
                  List
                </button>
                <button
                  type="button"
                  className="machine-view-toggle-btn"
                  aria-pressed="false"
                  disabled
                  title="Calendar view coming soon"
                >
                  Calendar
                </button>
              </fieldset>
            </div>
            <MachineTopbarActions
              activeTab={activeTab}
              facilityName={facilityName}
              onRegister={() => setRegisterOpen(true)}
              onScheduleMaintenance={() => setMaintenanceScheduleSignal(n => n + 1)}
            />
          </div>

          <nav
            className="machine-management-tabs"
            aria-label="Machine registry tabs"
          >
            <button
              type="button"
              className={`machine-management-tab ${
                activeTab === 'imaging' ? 'machine-management-tab--active' : ''
              }`}
              aria-current={activeTab === 'imaging' ? 'page' : undefined}
              onClick={() => handleTabChange('imaging')}
            >
              Imaging (RIS)
            </button>
            <button
              type="button"
              className={`machine-management-tab ${
                activeTab === 'maintenance' ? 'machine-management-tab--active' : ''
              }`}
              aria-current={activeTab === 'maintenance' ? 'page' : undefined}
              onClick={() => handleTabChange('maintenance')}
            >
              Maintenance
            </button>
          </nav>

          {activeTab === 'imaging' ? (
            <MachineImagingTabBody
              selectedMachine={selectedMachine}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              columnsOpen={columnsOpen}
              setColumnsOpen={setColumnsOpen}
              columnsRef={columnsRef}
              toggleableColumns={toggleableColumns}
              columnVisibility={columnVisibility}
              setColumnVisibility={setColumnVisibility}
              loading={loading}
              error={error}
              tableRows={tableRows}
              columns={columns}
              debouncedSearch={debouncedSearch}
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
              onRowClick={handleRowClick}
              onRegister={() => setRegisterOpen(true)}
              onClosePanel={() => setSelectedMachine(null)}
              onEditSuccess={handleEditSuccess}
              onConnectivityUpdate={handleMachineConnectivityRefresh}
              modalityOptions={modalityOptions}
              machines={machines}
            />
          ) : null}

          {activeTab === 'maintenance' ? (
            <MachineMaintenanceTab
              machines={machines}
              onNotify={handleMaintenanceNotify}
              scheduleOpenSignal={maintenanceScheduleSignal}
            />
          ) : null}
        </div>
      </div>

      <MachineRegisterDialog
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onSuccess={handleRegisterSuccess}
        modalityOptions={modalityOptions}
        existingMachines={machines}
      />
    </PageLayout>
  );
}

export default MachineManagement;
